import React from 'react';
import PropTypes from 'prop-types';
import { orderBy } from 'natural-orderby';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import TimeSeriesLineGraph from './TimeSeriesLineGraph';
import {
    formatTimeSeriesDataset,
    formatTimeSeriesDatasetMultiMetric,
} from '../../utils/timeSeriesDataFormatUtils';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getUniqueArray,
    getTimeSeriesTickFormatter,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics }) {
    if (metricRequests.length === 0 || metricRequests[0].metrics.length === 0) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([metricSource])) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

function processMultipleMetrics(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const dataLabels = metricRequest.metrics;

    const formattedData = formatTimeSeriesDatasetMultiMetric(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        metricRequest.metrics,
        numberDecimalFormatter,
        disconnectedGraph
    );

    return { dataLabels, formattedData };
}

function processSingleMetric(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const metricValueKey = metricRequest.metrics[0];
    const metricNameKey =
        metricRequest.groupBy.length === 0 ? null : metricRequest.groupBy[0];

    // prettier-ignore
    const dataLabels = !metricNameKey
        ? [metricValueKey]
        : orderBy(getUniqueArray(metricSource.metricSeries.map(
            metric => metric[metricNameKey]
        )));

    const formattedData = formatTimeSeriesDataset(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        dataLabels,
        metricNameKey,
        metricValueKey,
        numberDecimalFormatter,
        disconnectedGraph
    );

    return { dataLabels, formattedData };
}

export default function TimeSeriesLineGraphWrapper(props) {
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const dataFormatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : undefined;

    // prettier-ignore
    const { dataLabels, formattedData } =
        metricRequest.metrics.length > 1
            ? processMultipleMetrics(
                metricRequest,
                metricSource,
                props.numberDecimalFormatter,
                props.configuration.disconnectedGraph
            )
            : processSingleMetric(
                metricRequest,
                metricSource,
                props.numberDecimalFormatter,
                props.configuration.disconnectedGraph
            );

    return (
        <>
            {metricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <TimeSeriesLineGraph
                    {...props}
                    bucketTimes={metricSource.bucketTimes}
                    chartProperties={props.configuration}
                    dataFormatter={dataFormatter}
                    dataLabels={dataLabels}
                    dataPoints={formattedData}
                    dateFormatter={getTimeSeriesTickFormatter(
                        metricRequest.timeAndPrecision.precisionType
                    )}
                />
            </div>
        </>
    );
}
/* eslint-enable complexity */

TimeSeriesLineGraphWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
